import { gql } from '@apollo/client'

// The Evie page's thin voi-facts read (services/eviePage) — the replacement
// for EVE_PAGE_DATA_QUERY on the Evie path, and the tree router (evieEnabled
// answers without the giant legacy read).
export const EVIE_PAGE_FACTS_QUERY = gql`
  query EviePageFactsQuery($applicantId: Int!) {
    eviePageFacts(applicantId: $applicantId) {
      evieEnabled
      facts {
        currentPhase
        terminalState {
          isTerminal
          type
          canDoMore
          showConfetti
          ssnTypoOnly
          customFollowupCopy
        }
        autopilotExitedAt
        isInAutopilot
        allowImages
        isScreeningPending
        isCoapplicantSsnPending
        incomeConditionalInfo {
          isIncomeConditional
          shortfallCents
          currentIncomeCents
          requiredIncomeCents
        }
        conditionalDisplayInfo {
          isCecConditional
          conditionalStatus
          conditionalResult
        }
        frozenCreditInfo {
          attemptsUsed
          maxAttempts
          canRetry
          lastAttemptAt
        }
        notices
        screeningDocuments {
          id
          originalUploadTime
          name
          documentType
        }
        screeningEnabled
        criminalScreeningProvider
        pmPointOfContact
        applicantFirstName
        applicantFullName
        applicantUUID
        idvNoSelfie
        idvOutstanding
        siblingApplicantCount
        propertyDisplayName
        unitName
        rentCents
        initiationSteps {
          step
          completed
          createdAt
          applicantUUID
          noSelfie
          chatIdv
          skipped
          shouldAskSection8
          pullVoucherCreditForVisibility
          requiresTxSb17
          leaseGuaranteeDisclosureRequired
        }
        appFeeDeepLinkUrl
        completionReportExplanation
      }
    }
  }
`

// Same literal union as EveTypes' TerminalState (the modal's contract) —
// GraphQL serves it as String, narrowed here at the boundary.
export interface EvieTerminalState {
  isTerminal: boolean
  type:
    | 'success'
    | 'denied'
    | 'manual_review'
    | 'conditional'
    | 'timeout'
    | 'withdrawn'
    | 'ssn_fraud'
    | 'frozen_credit'
    | 'inactivity_timeout'
    | 'pending'
    | 'ssn_pending'
    | null
  canDoMore: boolean
  showConfetti: boolean
  ssnTypoOnly?: boolean | null
  customFollowupCopy: string | null
}

// Consent/TX SB 17 runs first when owed, then identity and deterministic
// personal-details confirmation before chat.
export interface EvieInitiationStep {
  step: 'identity' | 'consent' | 'personalInfo'
  completed: boolean
  createdAt: string | null
  applicantUUID: string | null
  noSelfie: boolean | null
  /** Identity step only: render the conversational (chat) IDV lane instead
   * of the classic Persona widget (experiment-gated; null reads as false). */
  chatIdv: boolean | null
  skipped: boolean | null
  shouldAskSection8: boolean | null
  pullVoucherCreditForVisibility?: boolean | null
  /** Fold the TX SB 17 § 5.253 attestation into the consent form (the
   * server derives the requirement; null reads as false). */
  requiresTxSb17: boolean | null
  /** Show the provider-data disclosure only for properties configured with
   * an external lease-guarantee program. */
  leaseGuaranteeDisclosureRequired?: boolean | null
}

export interface EvieScreeningDocument {
  id: number
  originalUploadTime: string | null
  name: string
  documentType: string | null
}

export interface EvieFacts {
  currentPhase: string
  terminalState: EvieTerminalState
  autopilotExitedAt: string | null
  isInAutopilot: boolean
  allowImages: boolean
  isScreeningPending: boolean
  isCoapplicantSsnPending: boolean
  incomeConditionalInfo: {
    isIncomeConditional: boolean
    shortfallCents: number
    currentIncomeCents: number
    requiredIncomeCents: number
  } | null
  conditionalDisplayInfo: {
    isCecConditional: boolean
    conditionalStatus: string | null
    conditionalResult: string | null
  } | null
  frozenCreditInfo: {
    attemptsUsed: number
    maxAttempts: number
    canRetry: boolean
    lastAttemptAt: string | null
  } | null
  notices: string[]
  screeningDocuments: EvieScreeningDocument[]
  screeningEnabled: boolean
  criminalScreeningProvider: string
  pmPointOfContact: string | null
  applicantFirstName: string
  applicantFullName: string | null
  /** Persona mount facts retained for deploy skew and the dormant fallback. */
  applicantUUID: string
  idvNoSelfie: boolean
  /** True while identity verification is still owed. IDV is voi-owned end
   * to end (Persona posts to voi), so this fact stays authoritative even
   * when the engine is down — it feeds EvieThread's engine-down
   * identity-card belt. */
  idvOutstanding: boolean
  siblingApplicantCount: number
  propertyDisplayName: string | null
  unitName: string | null
  rentCents: number | null
  initiationSteps: EvieInitiationStep[]
  appFeeDeepLinkUrl: string | null
  completionReportExplanation: string | null
}

export interface EviePageFactsResult {
  evieEnabled: boolean
  facts: EvieFacts | null
}
